import {
  AimOutlined,
  ArrowRightOutlined,
  CloseOutlined,
  FileTextOutlined,
  PlusOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Button, Collapse, message, Modal, Radio, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import {
  articleTemplateApi,
  TemplateMediaReplacementPreviewRes,
  TemplateMediaReplacementResultItem,
  TemplateMediaReplacementResultRes,
  TemplateMediaReplacementScope,
  TemplateSourceMediaItem,
  TemplateSourceMediaPageReq,
} from '@/axios/articleTemplate';
import { mediaBoxApi, MediaBoxChannelRes, MediaBoxItemRes, MediaBoxPageReq } from '@/axios/mediaBox';
import CustomSelect from '@/components/Base/Select';
import PaginationSelector from '@/components/Form/PaginationSelector';

import styles from './TemplateMediaReplacementDialog.module.less';

export interface SelectedArticleTemplate {
  templateId: string;
  templateName?: string;
}

interface TemplateMediaReplacementDialogProps {
  open: boolean;
  selectedTemplates: SelectedArticleTemplate[];
  onCancel: () => void;
  onSuccess: () => void | Promise<void>;
}

interface ReplacementGroupView {
  key: string;
  sources: TemplateSourceMediaItem[];
  target: MediaBoxItemRes;
}

const AVAILABILITY_MAP = {
  AVAILABLE: { text: '正常', color: 'success' },
  OFFLINE: { text: '已下线', color: 'warning' },
  MISSING: { text: '已删除', color: 'error' },
} as const;

const RESULT_STATUS_MAP: Record<TemplateMediaReplacementResultItem['status'], { text: string; color: string }> = {
  SUCCESS: { text: '成功', color: 'success' },
  SKIPPED: { text: '跳过', color: 'default' },
  FAILED: { text: '失败', color: 'error' },
  NOT_EXECUTED: { text: '未执行', color: 'warning' },
};

const formatPrice = (price?: number) => (price == null ? '-' : `￥${price}`);

const formatCostRange = (min?: number, max?: number) => {
  if (min == null && max == null) {
    return '-';
  }
  if (min === max || max == null) {
    return formatPrice(min);
  }
  if (min == null) {
    return formatPrice(max);
  }
  return `￥${min} - ￥${max}`;
};

const getScopeTemplateIds = (scope: TemplateMediaReplacementScope, templateIds: string[]) =>
  scope === 'SELECTED' ? templateIds : undefined;

const formatSourceMediaLabel = (item: TemplateSourceMediaItem) => {
  const availability = AVAILABILITY_MAP[item.availability]?.text || item.availability;
  const details = [formatPrice(item.price), item.displayChannel || item.channelType, availability].filter(Boolean);
  return `${item.mediaName}${details.map((detail) => `（${detail}）`).join('')}`;
};

const formatTargetMediaLabel = (item: MediaBoxItemRes) => `${item.mediaName}（${formatPrice(item.price)}）`;

const TemplateMediaReplacementDialog: React.FC<TemplateMediaReplacementDialogProps> = ({
  open,
  selectedTemplates,
  onCancel,
  onSuccess,
}) => {
  const [messageApi, messageContextHolder] = message.useMessage();
  const selectedTemplateIds = useMemo(
    () => Array.from(new Set(selectedTemplates.map((item) => String(item.templateId)))),
    [selectedTemplates]
  );
  const selectedTemplateKey = selectedTemplateIds.join(',');

  const [scope, setScope] = useState<TemplateMediaReplacementScope>('SELECTED');
  const [allTemplateCount, setAllTemplateCount] = useState<number>();
  const [selectedSourceMap, setSelectedSourceMap] = useState<Record<string, TemplateSourceMediaItem>>({});
  const [targetChannel, setTargetChannel] = useState<string>();
  const [selectedTarget, setSelectedTarget] = useState<MediaBoxItemRes>();
  const [channels, setChannels] = useState<MediaBoxChannelRes[]>([]);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [groups, setGroups] = useState<ReplacementGroupView[]>([]);
  const [preview, setPreview] = useState<TemplateMediaReplacementPreviewRes>();
  const [previewLoading, setPreviewLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [requestId, setRequestId] = useState('');
  const [result, setResult] = useState<TemplateMediaReplacementResultRes>();

  const sourceRecordCache = useRef<Record<string, TemplateSourceMediaItem>>({});
  const allCountRequestSequence = useRef(0);
  const previewSubmitting = useRef(false);
  const executeSubmitting = useRef(false);

  useEffect(
    () => () => {
      allCountRequestSequence.current += 1;
    },
    []
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    const sequence = ++allCountRequestSequence.current;
    articleTemplateApi
      .sourceMediaPage({
        scope: 'ALL',
        pageIndex: 1,
        pageSize: 10,
      })
      .then((response) => {
        if (sequence === allCountRequestSequence.current) {
          setAllTemplateCount(response.scopeTemplateCount);
        }
      })
      .catch(() => undefined);
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setChannelsLoading(true);
    mediaBoxApi
      .channels()
      .then(setChannels)
      .catch(() => undefined)
      .finally(() => setChannelsLoading(false));
  }, [open]);

  const loadSourceMedia = useCallback(async (params: TemplateSourceMediaPageReq) => {
    const response = await articleTemplateApi.sourceMediaPage(params);
    response.records.forEach((record) => {
      sourceRecordCache.current[String(record.mediaId)] = record;
    });
    if (response.scope === 'ALL') {
      setAllTemplateCount(response.scopeTemplateCount);
    }
    return response;
  }, []);

  const selectedSourceItems = useMemo(() => Object.values(selectedSourceMap), [selectedSourceMap]);
  const selectedSourceIds = useMemo(() => Object.keys(selectedSourceMap), [selectedSourceMap]);
  const usedSourceIds = useMemo(
    () => new Set(groups.flatMap((group) => group.sources.map((source) => String(source.mediaId)))),
    [groups]
  );
  const issueResults = useMemo(() => result?.results.filter((item) => item.status !== 'SUCCESS') || [], [result]);

  const clearDraft = () => {
    setSelectedSourceMap({});
    setSelectedTarget(undefined);
  };

  const changeScope = (nextScope: TemplateMediaReplacementScope) => {
    if (nextScope === scope) {
      return;
    }
    const hasConfiguration = groups.length > 0 || selectedSourceIds.length > 0 || Boolean(selectedTarget);
    sourceRecordCache.current = {};
    setScope(nextScope);
    setGroups([]);
    clearDraft();
    if (hasConfiguration) {
      messageApi.info('替换范围已变更，请重新配置替换媒体');
    }
  };

  const updateSelectedSources = (mediaIds: string[]) => {
    if (mediaIds.length > 100) {
      messageApi.warning('每个替换组最多选择 100 个原媒体');
      return;
    }
    setSelectedSourceMap((current) => {
      const next: Record<string, TemplateSourceMediaItem> = {};
      mediaIds.map(String).forEach((mediaId) => {
        const record = sourceRecordCache.current[mediaId] || current[mediaId];
        if (record) {
          next[mediaId] = record;
        }
      });
      return next;
    });
  };

  const buildDraftGroup = (): ReplacementGroupView | undefined => {
    if (!selectedSourceItems.length) {
      messageApi.warning('请选择需要被替换的原有媒体');
      return undefined;
    }
    if (!selectedTarget) {
      messageApi.warning('请选择替换后的目标媒体');
      return undefined;
    }
    if (selectedSourceItems.length > 100) {
      messageApi.warning('每个替换组最多选择 100 个原媒体');
      return undefined;
    }
    const targetId = String(selectedTarget.id);
    if (selectedSourceIds.includes(targetId)) {
      messageApi.warning('原媒体不可包含目标媒体');
      return undefined;
    }
    if (selectedSourceIds.some((mediaId) => usedSourceIds.has(mediaId))) {
      messageApi.warning('同一原媒体不能配置到多个替换组');
      return undefined;
    }
    return {
      key: uuidv4(),
      sources: selectedSourceItems,
      target: selectedTarget,
    };
  };

  const addGroup = () => {
    if (groups.length >= 20) {
      messageApi.warning('一次最多添加 20 个替换组');
      return;
    }
    const nextGroup = buildDraftGroup();
    if (!nextGroup) {
      return;
    }
    setGroups((current) => [...current, nextGroup]);
    clearDraft();
  };

  const createPreview = async () => {
    if (previewSubmitting.current) {
      return;
    }
    const hasPartialDraft =
      (selectedSourceItems.length > 0 && !selectedTarget) ||
      (selectedSourceItems.length === 0 && Boolean(selectedTarget));
    if (hasPartialDraft) {
      buildDraftGroup();
      return;
    }

    let finalGroups = groups;
    if (selectedSourceItems.length && selectedTarget) {
      if (groups.length >= 20) {
        messageApi.warning('一次最多添加 20 个替换组');
        return;
      }
      const draftGroup = buildDraftGroup();
      if (!draftGroup) {
        return;
      }
      finalGroups = [...groups, draftGroup];
      setGroups(finalGroups);
      clearDraft();
    }
    if (!finalGroups.length) {
      messageApi.warning('请添加至少一组替换');
      return;
    }

    previewSubmitting.current = true;
    setPreviewLoading(true);
    try {
      const response = await articleTemplateApi.previewMediaReplacement({
        scope,
        templateIds: getScopeTemplateIds(scope, selectedTemplateIds),
        replacementGroups: finalGroups.map((group) => ({
          sourceMediaIds: group.sources.map((source) => String(source.mediaId)),
          targetMediaId: String(group.target.id),
        })),
      });
      setPreview(response);
      setRequestId(uuidv4());
    } finally {
      previewSubmitting.current = false;
      setPreviewLoading(false);
    }
  };

  const executeReplacement = async () => {
    if (!preview || !requestId || executeSubmitting.current) {
      return;
    }
    executeSubmitting.current = true;
    setExecuting(true);
    try {
      const response = await articleTemplateApi.executeMediaReplacement({
        requestId,
        previewToken: preview.previewToken,
      });
      setPreview(undefined);
      try {
        await onSuccess();
      } catch {
        // 列表刷新失败不影响展示已完成的执行结果。
      }
      if (response.failedCount > 0 || response.skippedCount > 0) {
        setResult(response);
      } else {
        messageApi.success('媒体站点批量替换完成');
        onCancel();
      }
    } finally {
      executeSubmitting.current = false;
      setExecuting(false);
    }
  };

  const closeResult = () => {
    setResult(undefined);
    onCancel();
  };

  const resultColumns: ColumnsType<TemplateMediaReplacementResultItem> = [
    {
      title: '模板',
      dataIndex: 'templateName',
      width: 190,
      ellipsis: true,
      render: (value: string, record) => value || record.templateId,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 82,
      render: (status: TemplateMediaReplacementResultItem['status']) => {
        const option = RESULT_STATUS_MAP[status] || { text: status, color: 'default' };
        return <Tag color={option.color}>{option.text}</Tag>;
      },
    },
    {
      title: '替换项',
      dataIndex: 'replacedItemCount',
      width: 80,
    },
    {
      title: '替换前成本',
      width: 150,
      render: (_, record) => formatCostRange(record.beforeMinCost, record.beforeMaxCost),
    },
    {
      title: '替换后成本',
      width: 150,
      render: (_, record) => formatCostRange(record.afterMinCost, record.afterMaxCost),
    },
    {
      title: '说明',
      dataIndex: 'reason',
      ellipsis: true,
      render: (value?: string) => value || '-',
    },
  ];

  return (
    <>
      {messageContextHolder}
      <Modal
        className={styles.modal}
        open={open}
        title="媒体站点一键替换"
        width={880}
        centered
        footer={
          <div className={styles.modalFooter}>
            <div className={styles.footerHint}>
              <WarningOutlined />
              <span>可添加多组替换并行执行，每组原媒体将统一替换为目标媒体</span>
            </div>
            <Space>
              <Button disabled={previewLoading || executing} onClick={onCancel}>
                取消
              </Button>
              <Button type="primary" loading={previewLoading} disabled={executing} onClick={createPreview}>
                确认执行替换
              </Button>
            </Space>
          </div>
        }
        maskClosable={!previewLoading && !executing}
        keyboard={!previewLoading && !executing}
        onCancel={onCancel}
      >
        <div className={styles.scopeSection}>
          <Typography.Text strong className={styles.requiredTitle}>
            替换范围
          </Typography.Text>
          <Radio.Group
            className={styles.rangeOptions}
            value={scope}
            onChange={(event) => changeScope(event.target.value)}
          >
            <Radio value="SELECTED">
              仅替换表格当前选中的发文模板
              <span className={`${styles.countBadge} ${scope === 'SELECTED' ? styles.activeCountBadge : ''}`}>
                {selectedTemplateIds.length} 条
              </span>
            </Radio>
            <Radio value="ALL">
              替换全部发文模板
              <span className={`${styles.countBadge} ${scope === 'ALL' ? styles.activeCountBadge : ''}`}>
                {allTemplateCount == null ? '加载中' : `${allTemplateCount} 条`}
              </span>
            </Radio>
          </Radio.Group>
        </div>

        <Typography.Text strong className={`${styles.sectionTitle} ${styles.requiredTitle}`}>
          选择待替换媒体 → 目标媒体
        </Typography.Text>
        <div className={styles.workspace}>
          <section className={styles.mediaPanel}>
            <div className={styles.panelHeader}>
              <div className={styles.panelTitle}>
                <FileTextOutlined />
                <Typography.Text strong>待替换原媒体</Typography.Text>
              </div>
              <Typography.Text type="secondary">
                {selectedSourceIds.length ? `已选 ${selectedSourceIds.length} 个` : '多选'}
              </Typography.Text>
            </div>
            <div className={styles.selectorControl}>
              <PaginationSelector<TemplateSourceMediaItem, Pick<TemplateSourceMediaPageReq, 'scope' | 'templateIds'>>
                key={`source-${scope}-${selectedTemplateKey}`}
                fetchApi={(params) => loadSourceMedia(params as TemplateSourceMediaPageReq)}
                params={{
                  scope,
                  templateIds: getScopeTemplateIds(scope, selectedTemplateIds),
                }}
                pageSize={10}
                searchParamKey="mediaName"
                multiple
                value={selectedSourceIds}
                defaultOptions={selectedSourceItems.map((item) => ({
                  value: String(item.mediaId),
                  label: formatSourceMediaLabel(item),
                }))}
                optionTransform={(item) => ({
                  value: String(item.mediaId),
                  label: formatSourceMediaLabel(item),
                  disabled:
                    usedSourceIds.has(String(item.mediaId)) ||
                    String(selectedTarget?.id || '') === String(item.mediaId),
                })}
                placeholder="点击搜索选择原媒体"
                popupMatchSelectWidth
                listHeight={230}
                style={{ width: '100%' }}
                onChange={(values) => updateSelectedSources((values || []).map(String))}
              />
            </div>
          </section>

          <div className={styles.direction}>
            <ArrowRightOutlined />
          </div>

          <section className={styles.mediaPanel}>
            <div className={styles.panelHeader}>
              <div className={styles.panelTitle}>
                <AimOutlined />
                <Typography.Text strong>替换后目标媒体</Typography.Text>
              </div>
              <Typography.Text type="secondary">单选</Typography.Text>
            </div>
            <div className={styles.targetSelectors}>
              <CustomSelect
                allowClear
                showSearch
                optionFilterProp="label"
                loading={channelsLoading}
                value={targetChannel}
                options={channels.map((channel) => ({ label: channel.name, value: channel.code }))}
                placeholder="全部渠道"
                className={styles.channelSelect}
                onChange={(channel) => {
                  setTargetChannel(channel);
                  setSelectedTarget(undefined);
                }}
              />
              <div className={styles.targetSelector}>
                <PaginationSelector<MediaBoxItemRes, Pick<MediaBoxPageReq, 'channels' | 'availability'>>
                  key={`target-${targetChannel || 'all'}`}
                  fetchApi={(params) => mediaBoxApi.list(params)}
                  params={{
                    availability: 'AVAILABLE',
                    channels: targetChannel ? [targetChannel] : undefined,
                  }}
                  pageSize={10}
                  searchParamKey="mediaName"
                  value={selectedTarget ? String(selectedTarget.id) : undefined}
                  defaultOptions={
                    selectedTarget
                      ? [
                          {
                            value: String(selectedTarget.id),
                            label: formatTargetMediaLabel(selectedTarget),
                          },
                        ]
                      : []
                  }
                  optionTransform={(item) => ({
                    value: String(item.id),
                    label: formatTargetMediaLabel(item),
                    disabled:
                      selectedSourceIds.includes(String(item.id)) ||
                      item.price == null ||
                      (item.status != null && item.status !== 1),
                  })}
                  placeholder="点击搜索选择目标媒体"
                  popupMatchSelectWidth
                  placement="bottomRight"
                  listHeight={230}
                  style={{ width: '100%' }}
                  onChange={(value, item) => setSelectedTarget(value ? item : undefined)}
                />
              </div>
            </div>
          </section>
        </div>

        {groups.length > 0 && (
          <Typography.Text strong className={`${styles.sectionTitle} ${styles.requiredTitle} ${styles.groupsTitle}`}>
            已添加替换组（共 {groups.length} 组）
          </Typography.Text>
        )}
        <Button
          block
          type="dashed"
          icon={<PlusOutlined />}
          onClick={addGroup}
          disabled={groups.length >= 20}
          className={styles.addGroupButton}
        >
          添加当前选择为替换组
        </Button>

        {groups.length > 0 && (
          <div className={styles.groupList}>
            {groups.map((group, index) => (
              <div className={styles.groupItem} key={group.key}>
                <div className={styles.groupIndex}>{index + 1}</div>
                <div className={styles.groupMapping}>
                  <span className={styles.groupSourceNames}>
                    {group.sources.map((source) => source.mediaName).join('、')}
                  </span>
                  <ArrowRightOutlined className={styles.groupArrow} />
                  <span className={styles.groupTargetName}>{group.target.mediaName}</span>
                </div>
                <Button
                  type="text"
                  aria-label={`删除替换组 ${index + 1}`}
                  icon={<CloseOutlined />}
                  className={styles.removeGroupButton}
                  onClick={() => setGroups((current) => current.filter((item) => item.key !== group.key))}
                />
              </div>
            ))}
          </div>
        )}
      </Modal>

      <Modal
        className={styles.previewModal}
        open={Boolean(preview)}
        title={null}
        width={560}
        closable={false}
        centered
        footer={
          <div className={styles.confirmFooter}>
            <Button disabled={executing} onClick={() => setPreview(undefined)}>
              取消
            </Button>
            <Button type="primary" loading={executing} onClick={executeReplacement}>
              确认替换
            </Button>
          </div>
        }
        maskClosable={!executing}
        keyboard={!executing}
        onCancel={() => setPreview(undefined)}
      >
        {preview && (
          <div className={styles.confirmContent}>
            <div className={styles.confirmIcon}>
              <WarningOutlined />
            </div>
            <div className={styles.confirmTitle}>确认执行媒体替换</div>
            <div className={styles.confirmDescription}>
              确认执行以下 <strong>{preview.groups.length}</strong> 组媒体替换？
            </div>
            <div className={styles.confirmGroups}>
              {preview.groups.map((group) => (
                <div className={styles.confirmGroupItem} key={group.groupIndex}>
                  <span>{group.sourceMediaNames.join('、')}</span>
                  <ArrowRightOutlined />
                  <strong>{group.targetMediaName}</strong>
                </div>
              ))}
            </div>
            <div className={styles.confirmDetail}>
              替换范围：
              {preview.scope === 'SELECTED'
                ? `仅替换已勾选模板（${preview.scopeTemplateCount} 条）`
                : `全部模板（${preview.scopeTemplateCount} 条）`}
              ，其中 {preview.affectedTemplateCount} 条包含待替换媒体
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={Boolean(result)}
        title="媒体替换结果"
        width={940}
        closable={false}
        maskClosable={false}
        footer={
          <Button type="primary" onClick={closeResult}>
            完成
          </Button>
        }
      >
        {result && (
          <>
            <div className={styles.resultSummary}>
              <span>
                成功 <strong className={styles.successText}>{result.successCount}</strong>
              </span>
              <span>
                跳过 <strong>{result.skippedCount}</strong>
              </span>
              <span>
                失败 <strong className={styles.errorText}>{result.failedCount}</strong>
              </span>
            </div>
            <Collapse
              defaultActiveKey={['issues']}
              items={[
                {
                  key: 'issues',
                  label: `查看失败和跳过详情（${issueResults.length}）`,
                  children: (
                    <Table<TemplateMediaReplacementResultItem>
                      size="small"
                      rowKey={(record) => String(record.templateId)}
                      columns={resultColumns}
                      dataSource={issueResults}
                      pagination={false}
                      scroll={{ x: 850, y: 360 }}
                    />
                  ),
                },
              ]}
            />
          </>
        )}
      </Modal>
    </>
  );
};

export default TemplateMediaReplacementDialog;
